'use client';


import { useState, useCallback } from 'react';
import { HmacSHA256, HmacSHA512, HmacSHA1, HmacMD5, enc } from 'crypto-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, RefreshCw, FileText, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { hmacGeneratorContent } from '../../data/hmac-generator-content';
import Link from 'next/link';

// Import shadcn/ui components with proper fallbacks
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const algorithms = [
    { value: 'SHA-256', label: 'SHA-256' },
    { value: 'SHA-512', label: 'SHA-512' },
    { value: 'SHA-1', label: 'SHA-1' },
    { value: 'MD5', label: 'MD5' },
];

const outputEncodings = [
    { value: 'hex', label: 'Hexadecimal' },
    { value: 'base64', label: 'Base64' },
];

const sampleMessage = 'This is a sample message for HMAC generation.';
const sampleKey = 'your-secret-key-here';

export default function HMACGenerator() {
    const [message, setMessage] = useState('');
    const [key, setKey] = useState('');
    const [hmac, setHmac] = useState('');
    const [algorithm, setAlgorithm] = useState('SHA-256');
    const [outputEncoding, setOutputEncoding] = useState('hex');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const { toast } = useToast();

    const generateHMAC = useCallback(() => {
        if (!message.trim() || !key.trim()) {
            setHmac('');
            return;
        }

        try {
            setIsLoading(true);
            let hash;

            switch (algorithm) {
                case 'SHA-256':
                    hash = HmacSHA256(message, key);
                    break;
                case 'SHA-512':
                    hash = HmacSHA512(message, key);
                    break;
                case 'SHA-1':
                    hash = HmacSHA1(message, key);
                    break;
                case 'MD5':
                    hash = HmacMD5(message, key);
                    break;
                default:
                    hash = HmacSHA256(message, key);
            }

            const result = outputEncoding === 'base64'
                ? hash.toString(enc.Base64)
                : hash.toString(enc.Hex);

            setHmac(result);
        } catch (error) {
            console.error('Error generating HMAC:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate HMAC',
                variant: 'destructive',
            });
            setHmac('');
        } finally {
            setIsLoading(false);
        }
    }, [message, key, algorithm, outputEncoding, toast]);

    const handleCopy = () => {
        if (!hmac) return;

        navigator.clipboard.writeText(hmac)
            .then(() => {
                setIsCopied(true);
                toast({
                    title: 'Success',
                    description: 'HMAC copied to clipboard!',
                });
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch(() => {
                toast({
                    title: 'Error',
                    description: 'Failed to copy to clipboard',
                    variant: 'destructive',
                });
            });
    };

    const handleClear = () => {
        setMessage('');
        setKey('');
        setFileName(null);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const content = event.target?.result as string;
            setMessage(content);
        };
        reader.onerror = () => {
            toast({
                title: 'Error',
                description: 'Failed to read file',
                variant: 'destructive',
            });
        };
        reader.readAsText(file);
    };

    const handleUseSample = () => {
        setMessage(sampleMessage);
        setKey(sampleKey);
        setFileName(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        generateHMAC();
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <Toaster />
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="HMAC Generator">
                Generate HMAC (Hash-based Message Authentication Code) with our free online tool. Create secure authentication codes for API security.
            </ToolPageIntro>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Message</CardTitle>
                                <CardDescription>
                                    {fileName ? (
                                        <div className="flex items-center text-sm text-muted-foreground">
                                            <FileText className="h-4 w-4 mr-2" />
                                            {fileName}
                                        </div>
                                    ) : (
                                        'Enter the message you want to hash or upload a file'
                                    )}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Enter message to hash..."
                                    className="min-h-[120px] font-mono text-sm"
                                    disabled={isLoading}
                                />
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="file"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        id="file-upload"
                                        disabled={isLoading}
                                    />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        type="button"
                                        disabled={isLoading}
                                    >
                                        <label htmlFor="file-upload" className="cursor-pointer">
                                            <FileText className="h-4 w-4 mr-2" />
                                            {fileName ? 'Change File' : 'Upload File'}
                                        </label>
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        type="button"
                                        onClick={handleUseSample}
                                        disabled={isLoading}
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Use Sample
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Secret Key</CardTitle>
                                <CardDescription>
                                    Enter the secret key for HMAC generation
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Input
                                    type="text"
                                    value={key}
                                    onChange={(e) => setKey(e.target.value)}
                                    placeholder="Enter secret key..."
                                    className="font-mono"
                                    disabled={isLoading}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Settings</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="algorithm">Hash Algorithm</Label>
                                    <Select
                                        value={algorithm}
                                        onValueChange={setAlgorithm}
                                        disabled={isLoading}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select algorithm" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {algorithms.map((algo) => (
                                                <SelectItem key={algo.value} value={algo.value}>
                                                    {algo.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="encoding">Output Encoding</Label>
                                    <Select
                                        value={outputEncoding}
                                        onValueChange={setOutputEncoding}
                                        disabled={isLoading}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select encoding" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {outputEncodings.map((enc) => (
                                                <SelectItem key={enc.value} value={enc.value}>
                                                    {enc.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex justify-end">
                            <div className="flex space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleClear}
                                    disabled={isLoading || (!message && !key)}
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isLoading || !message || !key}
                                >
                                    {isLoading ? (
                                        <>
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate HMAC'
                                    )}
                                </Button>
                            </div>
                        </div>

                        {hmac && (
                            <Card>
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <CardTitle>HMAC {algorithm} Hash</CardTitle>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleCopy}
                                            disabled={!hmac}
                                        >
                                            {isCopied ? (
                                                <Check className="h-4 w-4 mr-2" />
                                            ) : (
                                                <Copy className="h-4 w-4 mr-2" />
                                            )}
                                            {isCopied ? 'Copied!' : 'Copy'}
                                        </Button>
                                    </div>
                                    <CardDescription>
                                        {outputEncoding === 'hex' ? 'Hexadecimal' : 'Base64'} encoded HMAC
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className={cn(
                                        'relative rounded-md border p-4 font-mono text-sm overflow-x-auto',
                                        'min-h-[100px] flex items-center',
                                        'bg-muted/50'
                                    )}>
                                        <pre className="whitespace-pre-wrap break-all">{hmac}</pre>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </form>

                <Card>
                    <CardHeader>
                        <CardTitle>About HMAC</CardTitle>
                    </CardHeader>
                    <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                        <p>
                            HMAC (Hash-based Message Authentication Code) is a specific type of message authentication code (MAC)
                            involving a cryptographic hash function and a secret cryptographic key. It can be used to verify both
                            the data integrity and the authenticity of a message.
                        </p>

                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 my-4 rounded-r">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h2a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        <strong>Security Note:</strong> While MD5 and SHA-1 are provided for compatibility,
                                        they are considered cryptographically broken and should be avoided for security-critical applications.
                                        Use SHA-256 or SHA-512 for better security.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <h3>How It Works</h3>
                        <ol className="list-decimal pl-5 space-y-2">
                            <li>Enter your message (or upload a file)</li>
                            <li>Provide a secret key</li>
                            <li>Select hash algorithm and output encoding</li>
                            <li>Click "Generate HMAC"</li>
                            <li>Copy the resulting HMAC hash</li>
                        </ol>

                        <h3>Common Uses</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>API authentication</li>
                            <li>Data integrity verification</li>
                            <li>Secure password storage (with proper key derivation)</li>
                            <li>Message authentication in network protocols</li>
                            <li>Digital signatures</li>
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={hmacGeneratorContent.title}
                    description={hmacGeneratorContent.description}
                    content={hmacGeneratorContent.content}
                    faqs={hmacGeneratorContent.faqs}
                    relatedTools={hmacGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}